import type {IWindowService} from 'angular';
import type {Logger} from 'core/logging/types';
import type {LogService} from 'core/logging/log.service';
import {exists} from '../utils/custom-utils';

declare global {
    interface Window {
        localizations: {[key: string]: string};
    }
}

/**
 * @ngdoc service
 * @name localizationService
 * @module portaro.services
 *
 * @description
 * See {@link localizationServiceProvider}
 */
export interface LocalizationService {
    get(key: string): string;
}

/**
 * @ngdoc provider
 * @name localizationServiceProvider
 * @module portaro.services
 *
 * @description
 * Service for getting localized messages.
 */
export default class LocalizationServiceProvider {
    public static readonly providerName = 'localizationService';

    public readonly $get: (logService: LogService) => LocalizationService;

    private $window: IWindowService;
    private logger: Logger;

    /*@ngInject*/
    constructor($windowProvider) {
        this.$window = $windowProvider.$get();

        this.logger = console;

        if (typeof this.$window.localizations !== 'object') {
            this.logger.warn('Probably you have not included localizations in your FTL');
        }

        this.$get = /*@ngInject*/ (logService: LogService) => this.createLocalizationService(logService);
    }

    /**
     * @ngdoc method
     * @name localizationServiceProvider#get
     *
     * @param {string} key Message key.
     *
     * @returns {string} Localized message key.
     *
     * @description
     * Method returns a localized message key. If the key is not present, it writes a warning to the console and returns the message key name.
     */
    public get(key: string): string {
        if (exists(this.$window.localizations) && exists(this.$window.localizations[key])) {
            return this.$window.localizations[key];
        }

        this.logger.warn(`Localization key '${key}' not found, fallback to key name`);

        return key;
    }

    private createLocalizationService(logService: LogService): LocalizationService {
        this.logger = logService;
        return this;
    }
}